export interface PwaUpdateInfo {
  registration: ServiceWorkerRegistration
  version?: string
}

interface PwaCallbacks {
  onOfflineReady?: () => void
  onUpdateAvailable?: (update: PwaUpdateInfo) => void
  onError?: (error: unknown) => void
}

export interface PwaRegistrationHandle {
  registration: ServiceWorkerRegistration
  dispose: () => void
}

interface VersionMessage {
  type: 'VERSION'
  version: string
}

function isVersionMessage(value: unknown): value is VersionMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'VERSION' &&
    'version' in value &&
    typeof value.version === 'string'
  )
}

async function readWorkerVersion(worker: ServiceWorker): Promise<string | undefined> {
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    let settled = false
    const finish = (version?: string) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeout)
      channel.port1.onmessage = null
      channel.port1.close()
      channel.port2.close()
      resolve(version)
    }
    const timeout = window.setTimeout(() => finish(), 1_500)
    channel.port1.onmessage = (event: MessageEvent<unknown>) => {
      finish(isVersionMessage(event.data) ? event.data.version : undefined)
    }
    try {
      worker.postMessage({ type: 'GET_VERSION' }, [channel.port2])
    } catch {
      finish()
    }
  })
}

async function announceUpdate(registration: ServiceWorkerRegistration, callbacks: PwaCallbacks) {
  const waitingWorker = registration.waiting
  if (!waitingWorker) return
  callbacks.onUpdateAvailable?.({
    registration,
    version: await readWorkerVersion(waitingWorker),
  })
}

function watchWorker(
  worker: ServiceWorker,
  registration: ServiceWorkerRegistration,
  callbacks: PwaCallbacks,
) {
  const onStateChange = () => {
    if (worker.state !== 'installed') return
    if (navigator.serviceWorker.controller) {
      void announceUpdate(registration, callbacks)
    } else {
      callbacks.onOfflineReady?.()
    }
  }
  worker.addEventListener('statechange', onStateChange)
  return () => worker.removeEventListener('statechange', onStateChange)
}

export async function registerPwa(
  callbacks: PwaCallbacks = {},
): Promise<PwaRegistrationHandle | undefined> {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
    return undefined
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    let disposed = false
    const workerDisposers = new Set<() => void>()
    const guardedCallbacks: PwaCallbacks = {
      onOfflineReady: () => {
        if (!disposed) callbacks.onOfflineReady?.()
      },
      onUpdateAvailable: (update) => {
        if (!disposed) callbacks.onUpdateAvailable?.(update)
      },
      onError: (error) => {
        if (!disposed) callbacks.onError?.(error)
      },
    }

    if (registration.waiting && navigator.serviceWorker.controller) {
      await announceUpdate(registration, guardedCallbacks)
    }

    const onUpdateFound = () => {
      if (registration.installing) {
        workerDisposers.add(watchWorker(registration.installing, registration, guardedCallbacks))
      }
    }
    registration.addEventListener('updatefound', onUpdateFound)

    const checkForUpdate = () => {
      if (navigator.onLine) void registration.update().catch(() => undefined)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate()
    }
    window.addEventListener('online', checkForUpdate)
    window.addEventListener('focus', checkForUpdate)
    document.addEventListener('visibilitychange', onVisibilityChange)
    const updateInterval = window.setInterval(checkForUpdate, 60 * 60 * 1_000)

    return {
      registration,
      dispose: () => {
        if (disposed) return
        disposed = true
        registration.removeEventListener('updatefound', onUpdateFound)
        window.removeEventListener('online', checkForUpdate)
        window.removeEventListener('focus', checkForUpdate)
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.clearInterval(updateInterval)
        workerDisposers.forEach((dispose) => dispose())
        workerDisposers.clear()
      },
    }
  } catch (error) {
    callbacks.onError?.(error)
    return undefined
  }
}

export function activateWaitingWorker(registration: ServiceWorkerRegistration): boolean {
  const waitingWorker = registration.waiting
  if (!waitingWorker) return false

  const onControllerChange = () => window.location.reload()
  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true })

  try {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
    return true
  } catch {
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    return false
  }
}
